import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiCalendar, FiPhoneCall, FiMapPin, FiMail, FiSend } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import SEO from "../components/SEO";
import SectionTitle from "../components/SectionTitle";
import PageHero from "../components/PageHero";
import api from "../utils/api";
import { buildWhatsAppLink } from "../utils/whatsapp";
import { SAURABH, GAURI } from "../utils/siteData";

const inputClass =
  "input-premium w-full px-4 py-2.5 rounded-lg border border-gold-300 bg-white/70";

export default function Contact() {
  const [searchParams] = useSearchParams();
  const initialService = searchParams.get("service") || "";
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: initialService,
    requestType: "Booking request",
    preferredDate: "",
    location: "",
    birthDetails: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [serviceOptions, setServiceOptions] = useState([]);

  useEffect(() => {
    api
      .get("/services/options")
      .then((res) => setServiceOptions(res.data))
      .catch(() => setServiceOptions([]));
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill in your name, phone and message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent successfully!");
      setForm({
        name: "",
        phone: "",
        email: "",
        service: initialService,
        requestType: "Booking request",
        preferredDate: "",
        location: "",
        birthDetails: "",
        message: "",
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const waLink = buildWhatsAppLink(SAURABH.whatsapp, "Namaste 🙏, I would like to enquire about your services.");

  return (
    <div>
      <SEO
        title="Contact Us"
        description="Contact Pandit Saurabh Shastri for pooja bookings or Gauri Shastri for astrology & palmistry consultations in Thane, Mumbai."
        keywords="contact pandit thane, book pooja thane, contact astrologer thane"
        path="/contact"
      />

      <PageHero
        eyebrow="Get in Touch"
        title="Contact Us"
        subtitle="We would love to hear from you. Reach out for bookings, queries or guidance."
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <SectionTitle eyebrow="Get in Touch" title="We're Here to Guide You" />

            <div className="space-y-6">
              <div className="bg-saffron-50 rounded-2xl p-6 border border-gold-100 shadow-premium hover-lift">
                <h3 className="font-heading text-lg text-maroon-800 mb-3">Saurabh Shastri - Pooja Services</h3>
                <div className="space-y-2 text-gray-700 text-sm">
                  <p className="flex items-center gap-2"><FiPhoneCall className="text-saffron-600" /> <a href={`tel:+91${SAURABH.phone}`} className="hover:text-saffron-700">{SAURABH.phoneDisplay}</a></p>
                  <p className="flex items-center gap-2"><FiMapPin className="text-saffron-600" /> Thane, Maharashtra</p>
                </div>
              </div>

              <div className="bg-maroon-50 rounded-2xl p-6 border border-gold-100 shadow-premium hover-lift">
                <h3 className="font-heading text-lg text-maroon-800 mb-3">Gauri Shastri - Astrology & Palmistry</h3>
                <div className="space-y-2 text-gray-700 text-sm">
                  <p className="flex items-center gap-2"><FiPhoneCall className="text-saffron-600" /> <a href={`tel:+91${GAURI.phone}`} className="hover:text-saffron-700">{GAURI.phoneDisplay}</a></p>
                  <p className="flex items-center gap-2"><FiMapPin className="text-saffron-600" /> Thane &amp; Online Worldwide</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-gray-700 text-sm">
                <FiMail className="text-saffron-600" /> <a href="mailto:info@tathastujotishalaya.com" className="hover:text-saffron-700">info@tathastujotishalaya.com</a>
              </div>

              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-full font-semibold shadow-premium-lg transition-colors"
              >
                <FaWhatsapp size={20} /> Chat on WhatsApp
              </a>
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden shadow-premium border border-gold-100 h-72">
              <iframe
                title="Thane Location Map"
                src="https://www.google.com/maps?q=Thane,Maharashtra&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-br from-gold-50 to-saffron-50 rounded-3xl p-8 border border-gold-200 shadow-premium">
            <h3 className="font-heading text-2xl text-maroon-800 mb-2">Booking & Consultation Request</h3>
            <p className="text-gray-600 text-sm mb-6">Share the details once so we can respond with the right timing, samagri and next steps.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Your Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="+91 9XXXXXXXXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Required</label>
                <select
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  className={`${inputClass} bg-white`}
                >
                  <option value="">Select a service</option>
                  {serviceOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Request Type</label>
                  <select
                    name="requestType"
                    value={form.requestType}
                    onChange={handleChange}
                    className={`${inputClass} bg-white`}
                  >
                    <option>Booking request</option>
                    <option>Muhurat guidance</option>
                    <option>Pricing enquiry</option>
                    <option>Online consultation</option>
                    <option>General question</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Date</label>
                  <div className="relative">
                    <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-saffron-600" />
                    <input
                      type="date"
                      name="preferredDate"
                      value={form.preferredDate}
                      onChange={handleChange}
                      className={`${inputClass} pl-10`}
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location / Consultation Mode</label>
                <input
                  type="text"
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Thane West, Mumbai, online video call, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Birth Details for Astrology (optional)</label>
                <textarea
                  name="birthDetails"
                  value={form.birthDetails}
                  onChange={handleChange}
                  rows={3}
                  className={inputClass}
                  placeholder="Date, exact time and place of birth, or note if palmistry is required."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  className={inputClass}
                  placeholder="Tell us about your requirement..."
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-maroon w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold disabled:opacity-60"
              >
                <FiSend /> {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
